import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import BackLink from "@/components/BackLink";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, FileText } from "lucide-react";
import { inr, formatDate, csvRow } from "@/lib/utils2";

export default function TransporterLedger() {
  const { id } = useParams();
  const [trans, setTrans] = useState(null);
  const [detail, setDetail] = useState({ trucks: [], on_account: 0 });
  const [payments, setPayments] = useState([]);

  useEffect(() => { (async () => {
    const [t, td, pp] = await Promise.all([
      api.get("/transporters"),
      api.get(`/transporter/${id}/trucks-detail`),
      api.get("/payments-made", { params: { transporter_id: id } }),
    ]);
    setTrans(t.data.find((x) => x.id === id));
    setDetail(td.data);
    setPayments(pp.data);
  })(); }, [id]);

  const { trucks, on_account } = detail;
  const totals = {
    transport: trucks.reduce((s, r) => s + r.transport_cost, 0),
    material: trucks.reduce((s, r) => s + r.material_cost, 0),
    owed: trucks.reduce((s, r) => s + r.total_owed, 0),
    paid: trucks.reduce((s, r) => s + r.paid, 0) + on_account,
    balance: trucks.reduce((s, r) => s + r.balance, 0) - on_account,
  };

  // Running ledger
  const rows = [
    ...trucks.map((r) => ({ date: r.entry_date, kind: "Truck Trip", ref: `${r.vehicle_number}${r.material_name ? ` · ${r.material_name}` : ""}`, credit: r.total_owed, debit: 0 })),
    ...payments.map((p) => ({ date: p.date, kind: "Payment", ref: p.mode + (p.particulars ? ` · ${p.particulars}` : ""), debit: p.amount, credit: 0 })),
  ].sort((a, b) => (a.date < b.date ? -1 : 1));
  let bal = 0;
  const withRunning = rows.map((r) => { bal += (r.credit || 0) - (r.debit || 0); return { ...r, balance: bal }; });

  const exportTrucksCsv = () => {
    const headers = ["Date", "Vehicle", "Party", "Material", "Weight", "Transport Cost", "Material Cost", "Total Owed", "Paid", "Balance", "Status"];
    const data = trucks.map((r) => [r.entry_date, r.vehicle_number, r.party_name, r.material_name, r.weight, r.transport_cost, r.material_cost, r.total_owed, r.paid, r.balance, r.status]);
    const csv = [headers, ...data].map(csvRow).join("");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `transporter-${trans?.name?.replace(/\s+/g, "_") || "ledger"}-trucks-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  const exportLedgerCsv = () => {
    const headers = ["Date", "Particulars", "Reference", "Owed (Credit)", "Paid (Debit)", "Running Balance"];
    const data = withRunning.map((r) => [r.date, r.kind, r.ref, r.credit, r.debit, r.balance]);
    const csv = [headers, ...data].map(csvRow).join("");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `transporter-${trans?.name?.replace(/\s+/g, "_") || "ledger"}-running-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1400px] mx-auto" data-testid="transporter-ledger-page">
      <div className="flex items-center justify-between mb-4">
        <BackLink to="/transporter-ledgers" label="Back" testId="transporter-ledger-back" />
        <div className="flex items-center gap-2">
        <Button onClick={exportLedgerCsv} data-testid="export-transporter-ledger-btn" className="rounded-sm bg-brand-forest text-white">
          <Download className="h-4 w-4 mr-1.5" /> Export Ledger CSV
        </Button>
        <Link to={`/transporter-settlement/${id}`}>
          <Button data-testid="open-transporter-settlement-btn" variant="outline" className="rounded-sm border-brand-ink">
            <FileText className="h-4 w-4 mr-1.5" /> Settlement Sheet
          </Button>
        </Link>
        </div>
      </div>
      <PageHeader title={trans?.name || "Transporter Ledger"} subtitle={trans?.address || ""} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <KPI label="Transport Owed" value={inr(totals.transport)} />
        <KPI label="Material Owed" value={inr(totals.material)} />
        <KPI label="Total Owed" value={inr(totals.owed)} />
        <KPI label="Paid" value={inr(totals.paid)} accent="text-brand-forest" />
        <KPI label="Balance" value={inr(totals.balance)} accent={totals.balance > 0 ? "text-brand-terracotta" : "text-brand-forest"} />
      </div>

      {on_account > 0 && (
        <div className="mb-6 p-4 bg-brand-sand/10 border border-brand-sand/40 rounded-sm text-sm">
          <span className="text-brand-muted">On-account payments (not yet linked to a truck): </span>
          <span className="font-semibold tabular-nums text-brand-terracotta">{inr(on_account)}</span>
        </div>
      )}

      {/* Truck-wise breakdown */}
      <Card className="rounded-sm border-brand-line shadow-none mb-8 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-brand-line">
          <div>
            <div className="font-serif text-xl text-brand-ink">Truck-wise Outstanding</div>
            <div className="text-xs text-brand-muted">Every trip for this transporter — Transport + Material cost, paid & balance</div>
          </div>
          <Button variant="outline" onClick={exportTrucksCsv} data-testid="export-trucks-csv-btn" className="rounded-sm">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Trucks CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
              <tr>
                <th className="text-left px-5 py-2.5">Date</th>
                <th className="text-left px-3 py-2.5">Vehicle</th>
                <th className="text-left px-3 py-2.5">Party</th>
                <th className="text-left px-3 py-2.5">Material</th>
                <th className="text-right px-3 py-2.5">Wgt</th>
                <th className="text-right px-3 py-2.5">Transport ₹</th>
                <th className="text-right px-3 py-2.5">Material ₹</th>
                <th className="text-right px-3 py-2.5">GST</th>
                <th className="text-right px-3 py-2.5">Total Owed</th>
                <th className="text-right px-3 py-2.5">Paid</th>
                <th className="text-right px-3 py-2.5">Balance</th>
                <th className="text-center px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {trucks.length === 0 && <tr><td colSpan={12} className="px-5 py-10 text-center text-brand-muted text-sm">No trips for this transporter yet.</td></tr>}
              {trucks.map((r) => (
                <tr key={r.truck_id} className="border-t border-brand-line hover:bg-secondary/40" data-testid={`tt-row-${r.truck_id}`}>
                  <td className="px-5 py-2.5 whitespace-nowrap">{formatDate(r.entry_date)}</td>
                  <td className="px-3 py-2.5 font-medium">{r.vehicle_number}</td>
                  <td className="px-3 py-2.5 text-brand-muted max-w-[140px] truncate" title={r.party_name}>{r.party_name}</td>
                  <td className="px-3 py-2.5 text-brand-muted max-w-[140px] truncate" title={r.material_name}>{r.material_name || "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{r.weight}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{inr(r.transport_cost)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{inr(r.material_cost)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {r.purchase_gst_amount > 0 ? (
                      <span title={`${r.purchase_gst_percent}% · Inv: ${r.purchase_gst_invoice_number || "—"}`} className="text-brand-sand font-medium">
                        {inr(r.purchase_gst_amount)}
                      </span>
                    ) : <span className="text-brand-muted">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{inr(r.total_owed)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-brand-forest">{inr(r.paid)}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${r.balance > 0 ? "text-brand-terracotta" : "text-brand-muted"}`}>{inr(r.balance)}</td>
                  <td className="px-3 py-2.5 text-center">
                    <Badge className={`rounded-sm text-[10px] tracking-[0.1em] ${
                      r.status === "SETTLED" ? "bg-brand-forest text-white" :
                      r.status === "PARTIAL" ? "bg-brand-sand/30 text-brand-terracotta" :
                      "bg-brand-terracotta/10 text-brand-terracotta border-brand-terracotta"
                    }`}>{r.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
            {trucks.length > 0 && (
              <tfoot className="bg-secondary/40 border-t-2 border-brand-ink text-xs">
                <tr>
                  <td colSpan={5} className="px-5 py-2.5 uppercase tracking-[0.12em] text-brand-muted">Totals</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{inr(totals.transport)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{inr(totals.material)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-brand-sand">{inr(trucks.reduce((s, r) => s + (r.purchase_gst_amount || 0), 0))}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{inr(totals.owed)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-brand-forest">{inr(trucks.reduce((s, r) => s + r.paid, 0))}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{inr(trucks.reduce((s, r) => s + r.balance, 0))}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {/* Running ledger */}
      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <div className="px-5 py-4 border-b border-brand-line">
          <div className="font-serif text-xl text-brand-ink">Running Ledger</div>
          <div className="text-xs text-brand-muted">Chronological credits (trips) and debits (payments)</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="text-left px-5 py-2.5">Date</th>
              <th className="text-left px-3 py-2.5">Particulars</th>
              <th className="text-left px-3 py-2.5">Ref</th>
              <th className="text-right px-3 py-2.5">Purchase</th>
              <th className="text-right px-3 py-2.5">Paid</th>
              <th className="text-right px-5 py-2.5">Balance</th>
            </tr>
          </thead>
          <tbody>
            {withRunning.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-brand-muted text-sm">No transactions yet.</td></tr>}
            {withRunning.map((r, i) => (
              <tr key={i} className="border-t border-brand-line">
                <td className="px-5 py-2.5 whitespace-nowrap">{formatDate(r.date)}</td>
                <td className="px-3 py-2.5">{r.kind}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{r.ref}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{r.credit ? inr(r.credit) : "—"}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-brand-forest">{r.debit ? inr(r.debit) : "—"}</td>
                <td className="px-5 py-2.5 text-right tabular-nums font-medium">{inr(r.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <Card className="rounded-sm border-brand-line shadow-none p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">{label}</div>
      <div className={`font-serif text-xl mt-2 tabular-nums ${accent || "text-brand-ink"}`}>{value}</div>
    </Card>
  );
}
